import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Popup, Search } from "semantic-ui-react";

import { useSelectedLocation } from "components";
import { typedQueryClient } from "hooks";
import { createFormHelper } from "lib/Form";
import { CUSTOMER_TYPES } from "models";
import { SEARCH_TAGS } from "modules/Appointments/components/NewAppointment";
import { useSearchCarQuery, useSearchCustomerByCarQuery } from "modules/Appointments/components/NewAppointment/hooks";
import { DEBOUNCED_SEARCH_TIME, MIN_SEARCH_LENGTH, formatRegistrationNumber } from "modules/Appointments/components/NewAppointment/utils";
import { AppointmentsQueryKeys } from "modules/Appointments/queryKeys";
import { debounce, isKeyLoopLocation } from "utils/common";

import type { Customer } from "models";
import type { AppointmentSection } from "modules/Appointments/components/NewAppointment";
import type { SearchResultData } from "semantic-ui-react";

type CustomerResultSelectType = {
  customer?: Customer;
  customerType?: CUSTOMER_TYPES;
};

type SearchCarResultsType = {
  id: number;
  key: number;
  title: string;
  car: { id: number; reg_nr: string; vin_nr: string; make: string; model: string; dms_nr: string; apk_date_dms: string | null };
};

type CarSearchProps = {
  lastSelectedTitle: string;
  carSearchTerm: string;
  showCarTag: SEARCH_TAGS | null;
  handleSetCarSearchTerm: (value: string) => void;
  handleSetCarFromExternalData: (value: boolean) => void;
  handleSetShowCarTag: (value: SEARCH_TAGS | null) => void;
  handleSetLastSelectedTitle: (value: string) => void;
  handleSetDisabledOwnerSearch: (value: boolean) => void;
  handleSetDisabledDriverSearch: (value: boolean) => void;
  handleSetDisabledContractorSearch: (value: boolean) => void;
  handleSetCustomerOwnerPreselect: (value: boolean) => void;
  handleSetCustomerDriverPreselect: (value: boolean) => void;
  handleSetCustomerContractorPreselect: (value: boolean) => void;
  handleCustomerResultSelect: ({ customer, customerType }: CustomerResultSelectType) => void;
};

const FormHelper = createFormHelper<AppointmentSection>();

export const CarSearch = ({
  showCarTag,
  carSearchTerm,
  lastSelectedTitle,
  handleSetShowCarTag,
  handleSetCarSearchTerm,
  handleSetLastSelectedTitle,
  handleSetCarFromExternalData,
  handleCustomerResultSelect,
  handleSetCustomerOwnerPreselect,
  handleSetCustomerDriverPreselect,
  handleSetCustomerContractorPreselect,
  handleSetDisabledOwnerSearch,
  handleSetDisabledDriverSearch,
  handleSetDisabledContractorSearch
}: CarSearchProps) => {
  const { t } = useTranslation();

  const [isCarNameTooLong, setIsCarNameTooLong] = useState(false);
  const { selectedLocation } = useSelectedLocation();
  const { setValue, watch } = FormHelper.useFormContext();
  const regNr = watch("car.reg_nr");
  const carSearchWrapperRef = useRef<HTMLDivElement>(null);

  const { data: customersByCar, refetch: refetchCustomersByCar } = useSearchCustomerByCarQuery(regNr);
  const { data: cars, refetch: refetchCars, isFetching: isFetchingCarSearch } = useSearchCarQuery({ regNr: carSearchTerm });

  useEffect(() => {
    if (regNr) refetchCustomersByCar();
  }, [regNr]);

  useEffect(() => {
    if (!customersByCar) return;

    const customerOwners = customersByCar.filter(({ customer_type }) => customer_type === CUSTOMER_TYPES.Owner);
    const customerDrivers = customersByCar.filter(({ customer_type }) => customer_type === CUSTOMER_TYPES.Driver);
    const customerContractors = customersByCar.filter(({ customer_type }) => customer_type === CUSTOMER_TYPES.Contractor);

    if (customerDrivers.length === 1) {
      handleCustomerResultSelect({ customer: customerDrivers.at(0) });
      handleSetCustomerDriverPreselect(true);
      handleSetDisabledDriverSearch(true);
    } else {
      handleCustomerResultSelect({ customerType: CUSTOMER_TYPES.Driver });
      handleSetCustomerDriverPreselect(false);
      handleSetDisabledDriverSearch(false);
    }

    if (customerContractors.length === 1) {
      handleCustomerResultSelect({ customer: customerContractors.at(0) });
      handleSetCustomerContractorPreselect(true);
      handleSetDisabledContractorSearch(true);
    } else {
      handleCustomerResultSelect({ customerType: CUSTOMER_TYPES.Contractor });
      handleSetCustomerContractorPreselect(false);
      handleSetDisabledContractorSearch(false);
    }

    if (customerOwners.length === 1) {
      handleCustomerResultSelect({ customer: customerOwners.at(0) });
      handleSetCustomerOwnerPreselect(true);
      handleSetDisabledOwnerSearch(true);
    } else {
      handleCustomerResultSelect({ customerType: CUSTOMER_TYPES.Owner });
      handleSetCustomerOwnerPreselect(false);
      handleSetDisabledOwnerSearch(false);
    }
  }, [customersByCar]);

  const handleResetCarValues = () => {
    setValue("car.id", -1);
    setValue("car.reg_nr", "");
    setValue("car.make", "");
    setValue("car.model", "");
    setValue("car.vin_nr", "");
    setValue("car.apk_date_dms", "");
    setValue("car.dms_nr", "");
  };

  const handleCarSearch = async (searchTerm?: string) => {
    if (!searchTerm) {
      handleResetCarValues();
      handleSetCustomerDriverPreselect(false);
      handleSetCustomerOwnerPreselect(false);
      handleSetCustomerContractorPreselect(false);
      handleSetCustomerDriverPreselect(false);
      handleSetDisabledDriverSearch(false);
      handleSetDisabledOwnerSearch(false);
    }

    if (searchTerm && searchTerm.length >= MIN_SEARCH_LENGTH) {
      const { data: searchedCars } = await refetchCars();
      handleSetCustomerDriverPreselect(false);
      handleSetDisabledDriverSearch(false);
      handleSetDisabledOwnerSearch(false);
      if (!searchedCars?.length) setValue("car.reg_nr", searchTerm);
    }
  };

  const debouncedCarSearch = debounce((value: string) => handleCarSearch(value), DEBOUNCED_SEARCH_TIME);

  const handleCarSearchChange = (value: string) => {
    const carSearchTerm = isKeyLoopLocation(selectedLocation) ? formatRegistrationNumber(value) : value;
    handleSetCarSearchTerm(carSearchTerm);
    typedQueryClient.resetQueries(AppointmentsQueryKeys.searchCars);
    debouncedCarSearch(carSearchTerm);
    handleResetCarValues();
  };

  const handleCarResultSelect = (data: SearchResultData) => {
    const carResult: SearchCarResultsType = data.result;

    setValue("car.id", carResult.id);
    setValue("car.reg_nr", carResult.car.reg_nr);
    setValue("car.make", carResult.car.make);
    setValue("car.model", carResult.car.model);
    setValue("car.vin_nr", carResult.car.vin_nr);
    setValue("car.apk_date_dms", carResult.car.apk_date_dms);
    setValue("car.dms_nr", carResult.car.dms_nr);

    handleSetCustomerDriverPreselect(false);
    handleSetDisabledDriverSearch(false);
    handleSetDisabledOwnerSearch(false);
  };

  const handleCarSearchOnBlur = () => {
    if (lastSelectedTitle) handleSetCarSearchTerm(lastSelectedTitle);
    else if (!carSearchTerm && regNr) handleSetCarSearchTerm(regNr);
  };

  const handleOnCarSearchChange = (value?: string) => {
    handleSetShowCarTag(null);
    handleCarSearchChange(String(value));

    if (value !== lastSelectedTitle) {
      handleSetCarFromExternalData(false);
      handleSetLastSelectedTitle("");
    }

    if (!value) setValue("car.reg_nr", "");
  };

  const handleOnCarResultSelect = (data: SearchResultData) => {
    const carResult: SearchCarResultsType = data.result;

    handleSetCarSearchTerm(carResult.car.reg_nr);
    handleSetShowCarTag(carResult.car.dms_nr.startsWith("claire_") ? SEARCH_TAGS.Claire : SEARCH_TAGS.DMS);
    handleCarResultSelect(data);
    handleSetLastSelectedTitle(carResult.title);

    window.setTimeout(() => {
      const input = carSearchWrapperRef.current?.querySelector("input");
      if (input) input.blur();
    }, DEBOUNCED_SEARCH_TIME);
  };

  const handleOnCarFocus = () => {
    if (regNr) handleSetCarSearchTerm(regNr);
  };

  useEffect(() => {
    const input = carSearchWrapperRef.current?.querySelector("input");
    return setIsCarNameTooLong(input ? input.scrollWidth > input.clientWidth : false);
  }, [lastSelectedTitle, carSearchTerm]);

  return (
    <Popup
      hoverable
      content={lastSelectedTitle || carSearchTerm}
      disabled={!lastSelectedTitle || !isCarNameTooLong}
      trigger={
        <div className="search-wrapper" ref={carSearchWrapperRef}>
          <Search
            fluid
            results={cars ?? []}
            value={lastSelectedTitle || carSearchTerm}
            loading={isFetchingCarSearch}
            minCharacters={MIN_SEARCH_LENGTH}
            onFocus={handleOnCarFocus}
            onBlur={handleCarSearchOnBlur}
            icon={<Icon className="magnifying glass" />}
            onResultSelect={(_, data) => handleOnCarResultSelect(data)}
            onSearchChange={(_, { value }) => handleOnCarSearchChange(value)}
            noResultsMessage={t("v8_no_results", "No results found")}
            resultRenderer={({ title, car: { dms_nr } }) => (
              <div className="NewAppointment__SearchResult">
                <p>{title}</p>
                <div className="NewAppointment__SearchResult__tag">{dms_nr.startsWith("claire_") ? "Claire" : "DMS"}</div>
              </div>
            )}
          />
          {showCarTag && <div className="absolute-tag">{showCarTag}</div>}
        </div>
      }
    />
  );
};
